import _ from "lodash";
import React, { createRef } from "react";
import classes from "./LoginFormV2.scss";
import { FormTextInput } from "UIComponents/InputTextField";
import PhoneInputField from "UIComponents/PhoneInputField";
import I18nHOC from "UIComponents/I18nHOC";
import { Button } from "@toddle-edu/ds-web";
import { RefreshArrowOutlined } from "@toddle-edu/ds-icons";
import { onEnterOrSpaceKeyDown } from "Utils";

const styles = {
  editContainerStyle: {
    display: "block",
  },
};

class LoginFormV2 extends React.PureComponent {
  constructor(props) {
    super(props);
    const {
      data: { email = "", RESEND_TIMER },
    } = props;
    this.state = {
      isSent: false,
      timer: RESEND_TIMER,
      isResendEnabled: false,
      isStudentOTPSent: !_.isEqual(email, ""),
    };
    this.inputRefs = {};
    this.buttonRef = createRef();
  }

  UNSAFE_componentWillMount = () => {
    const {
      resetScreen,
      loginData: {
        email = "",
        phoneNo = "",
        shouldRenderPhoneInput = false,
      } = {},
    } = this.props;

    resetScreen("loginFormV2", {
      email,
      phoneNo,
      shouldRenderPhoneInput,
      otp: "",
    });
  };

  componentDidMount = () => {
    const { loginData: { email = "" } = {}, userType } = this.props;

    if (userType !== "student") {
      if (email.length > 0) {
        this.inputRefs?.otp?.focus();
      }
      this.startTimer();
    } else {
      this.inputRefs?.email?.focus();
    }

    document.addEventListener("keydown", this.handleKeyDown);
  };

  componentWillUnmount() {
    clearInterval(this.intervalId);
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  startTimer = () => {
    this.intervalId = setInterval(() => {
      this.setState(
        prevState => ({ timer: prevState.timer - 1 }),
        () => {
          if (this.state.timer === 0) {
            clearInterval(this.intervalId);
            this.setState({ isResendEnabled: true });
          }
        }
      );
    }, 1000);
  };

  resetOTP = () => {
    const { updateInputField } = this.props;
    updateInputField({ otp: "" });
  };

  focusInputField = name => {
    this.inputRefs[name]?.focus();
  };

  handleResendClick = async () => {
    const {
      generateOtpForSignIn,
      generateEmailOTPForSignIn,
      userType,
      role,
      data: { shouldRenderPhoneInput },
    } = this.props;

    const { isSuccess } = shouldRenderPhoneInput
      ? await generateOtpForSignIn()
      : await generateEmailOTPForSignIn({ userType, role });

    const { RESEND_TIMER } = this.props.data;

    if (isSuccess) {
      this.resetOTP();
      this.focusInputField("otp");
      this.setState({ timer: RESEND_TIMER, isResendEnabled: false }, () => {
        this.startTimer();
      });
    }
  };

  isValid = () => {
    const refKeys = Object.keys(this.inputRefs);
    let errorCount = 0;
    _.forEach(refKeys, key => {
      if (this.inputRefs[key]) {
        errorCount += this.inputRefs[key].isValid();
      }
    });

    return errorCount;
  };

  onSignInWithOTPClick = async () => {
    const {
      onSignInWithOTPClick,
      showErrorToast,
      verifyParentOTPForSignIn,
      data: { shouldRenderPhoneInput, email },
      userType,
      generateEmailOTPForSignIn,
      updateInputField,
      updateCurrentScreen,
      isOTPLoginEnabled,
    } = this.props;

    const errorCount = this.isValid();
    showErrorToast({ errorCount });
    if (errorCount > 0) {
      return;
    }

    if (userType === "student" && !this.state.isStudentOTPSent) {
      if (isOTPLoginEnabled) {
        const { isSuccess } = await generateEmailOTPForSignIn({
          userType,
        });
        const { RESEND_TIMER } = this.props.data;

        if (isSuccess) {
          this.setState(
            {
              timer: RESEND_TIMER,
              isResendEnabled: false,
              isStudentOTPSent: true,
            },
            () => {
              this.startTimer();
            }
          );
        }
      } else {
        updateInputField({ email }, "loginForm");
        updateCurrentScreen({ type: "loginForm" });
      }
    } else {
      shouldRenderPhoneInput
        ? verifyParentOTPForSignIn()
        : onSignInWithOTPClick();
    }
  };

  onBackClick = () => {
    const {
      updateCurrentScreen,
      userType,
      role,
      updateInputField,
    } = this.props;

    updateInputField({});
    updateInputField({}, "loginForm");

    switch (userType) {
      case "staff": {
        if (role === "visitor") {
          updateCurrentScreen({ type: "visitorLogin" });
        } else {
          updateCurrentScreen({ type: "teacherLogin" });
        }
        break;
      }
      case "student":
        updateCurrentScreen({ type: "studentLogin" });
        break;
      case "parent":
        updateCurrentScreen({ type: "parentLogin" });
        break;
    }
  };

  updateInputRef = (key, ref) => {
    this.inputRefs[key] = ref;
  };

  getHeaderText = () => {
    const { userType, role } = this.props;
    switch (userType) {
      case "student":
        return "Student account";
      case "staff":
        if (role === "visitor") {
          return "Visitor account";
        }
        return "Teacher account";
      case "parent":
        return "Family account";
    }
  };

  checkPhoneVerification = value => {
    return value.length < 6 ? "Please enter a valid phone number" : "";
  };

  handleChangeInput = () => {
    const { userType, role, updateCurrentScreen } = this.props;

    switch (userType) {
      case "staff": {
        if (role === "visitor") {
          updateCurrentScreen({ type: "visitorLogin" });
        } else {
          updateCurrentScreen({ type: "teacherLogin" });
        }
        break;
      }
      case "student":
        this.setState({ isStudentOTPSent: false });
        break;
      case "parent":
        updateCurrentScreen({ type: "parentLogin" });
        break;
    }
  };

  handleKeyDown = event => {
    if (event.key === "Enter" && this.buttonRef.current) {
      this.buttonRef.current.click();
    }
  };

  render() {
    const {
      updateInputField,
      data: {
        email,
        otp = "",
        phoneNo = "",
        shouldRenderPhoneInput = false,
      } = {},
      checkEmailVerification,
      renderBackButtonComponent,
      customStyle: { headerStyleV2 } = {},
      updateCurrentScreen,
      renderErrorComponent,
      inputStyle,
      userType,
      isPasswordLoginEnabled,
      renderTradeMarkText,
      isOrgWorkspaceLoginPage = false,
      t,
    } = this.props;

    const shouldShowOTPInput = !(
      userType === "student" && !this.state.isStudentOTPSent
    );

    const containerStyle = {
      paddingTop: isOrgWorkspaceLoginPage ? "13%" : "19.5%",
    };

    return (
      <div className={classes.container} style={containerStyle}>
        <div>
          {renderBackButtonComponent({
            onClick: this.onBackClick,
            buttonText: "Go back",
          })}
          <div style={headerStyleV2}>{this.getHeaderText()}</div>
          <div className={classes.innerContainer}>
            <div className={classes.inputsContainer}>
              <div className={classes.inputContainer}>
                {userType === "parent" && shouldRenderPhoneInput ? (
                  <>
                    <div className={classes.labelContainer}>
                      <div className={classes.customLabel}>
                        <div>Phone number*</div>
                      </div>
                      <div className={classes.labelSuffixContainer}>
                        <span
                          className={classes.labelSuffixText}
                          onClick={this.handleChangeInput}
                          onKeyDown={e => {
                            onEnterOrSpaceKeyDown(e, this.handleChangeInput);
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label={t("a11y:change_phone_number")}
                        >
                          {"Change phone number"}
                        </span>
                      </div>
                    </div>
                    <PhoneInputField
                      value={phoneNo}
                      placeholder={"Enter your phone number"}
                      name={"phoneNo"}
                      key={"phoneNo"}
                      updateInputField={({ phoneNo }) => {
                        updateInputField({ phoneNo });
                      }}
                      ref={ref => this.updateInputRef("phoneNo", ref)}
                      renderErrorComponent={renderErrorComponent}
                      error={"Enter valid phone number"}
                      customValidation={this.checkPhoneVerification}
                      editContainerStyle={styles.editContainerStyle}
                      disabled={true}
                    />
                  </>
                ) : (
                  <FormTextInput
                    size={"large"}
                    textInputLabel={"Email*"}
                    value={email}
                    placeholder={"Enter your email"}
                    name={"email"}
                    updateInputField={updateInputField}
                    customValidation={checkEmailVerification}
                    ref={ref => this.updateInputRef("email", ref)}
                    error={"Enter email address"}
                    autoComplete={"off"}
                    disabled={shouldShowOTPInput}
                    onEnter={this.onSignInWithOTPClick}
                    labelSuffix={
                      shouldShowOTPInput ? (
                        <span
                          className={classes.labelSuffixText}
                          onClick={this.handleChangeInput}
                          onKeyDown={e => {
                            onEnterOrSpaceKeyDown(e, this.handleChangeInput);
                          }}
                          role="button"
                          tabIndex="0"
                          aria-label={t("a11y:change_email")}
                        >
                          {"Change email"}
                        </span>
                      ) : null
                    }
                  />
                )}
              </div>
              {shouldShowOTPInput ? (
                <div className={classes.inputContainer}>
                  <FormTextInput
                    testId="input-login-otp"
                    textInputLabel={"OTP*"}
                    ref={ref => this.updateInputRef("otp", ref)}
                    value={otp}
                    name={"otp"}
                    type={"password"}
                    placeholder={"• • • • • •"}
                    updateInputField={updateInputField}
                    error={"Enter valid OTP"}
                    size={"large"}
                    autoComplete={"off"}
                    autoFocus={true}
                    letterSpacing={"10px"}
                    labelSuffix={
                      userType === "parent" &&
                      shouldRenderPhoneInput ? null : isPasswordLoginEnabled ? (
                        <span
                          className={classes.labelSuffixText}
                          onClick={() => {
                            updateInputField({ email }, "loginForm");
                            updateCurrentScreen({ type: "loginForm" });
                          }}
                          data-test-id="input-login-sigin-with-password"
                          role="button"
                          tabIndex="0"
                          aria-label={t("signin:sign_in_using_password")}
                          onKeyDown={e => {
                            onEnterOrSpaceKeyDown(e, () => {
                              updateInputField({ email }, "loginForm");
                              updateCurrentScreen({ type: "loginForm" });
                            });
                          }}
                        >
                          {"Sign in using password"}
                        </span>
                      ) : null
                    }
                  />
                </div>
              ) : null}
            </div>
            {shouldShowOTPInput ? (
              <div className={classes.middleContainer}>
                <Button
                  className={classes.forgotPasswordBtn}
                  data-test-id="button-send-again"
                  variant={"inline-progressive"}
                  size={"small"}
                  tabIndex={this.state.isResendEnabled ? 0 : -1}
                  onClick={this.handleResendClick}
                  disabled={!this.state.isResendEnabled}
                  onKeyDown={e => {
                    onEnterOrSpaceKeyDown(e, this.handleResendClick);
                  }}
                >
                  <RefreshArrowOutlined
                    variant={"link"}
                    disabled={!this.state.isResendEnabled}
                  />
                  <span className={classes.resendTextWrapper}>{`Send again ${
                    this.state.isResendEnabled ? "" : `(${this.state.timer}s)`
                  }`}</span>
                </Button>
              </div>
            ) : null}
            <div className={classes.bottomContainer}>
              <Button
                data-test-id="button-sign-in"
                variant={"destructive"}
                size={"large"}
                onClick={this.onSignInWithOTPClick}
                isFullWidth={true}
                ref={this.buttonRef}
              >
                {shouldShowOTPInput ? "Sign in" : "Next"}
              </Button>
            </div>
          </div>
        </div>
        <div>
          {renderTradeMarkText({
            align: "left",
          })}
        </div>
      </div>
    );
  }
}

export default I18nHOC({ resource: ["a11y", "signin"] })(LoginFormV2);
